import axios from "axios";
import createApp from "@shopify/app-bridge";
import { getSessionToken } from "@shopify/app-bridge/utilities/session-token";

const CONTEXT_STORAGE_KEY = "shopify-app-context";
const LOCAL_HOSTS = new Set(["localhost", "127.0.0.1"]);

/** Shopify App Bridge API keys by shop domain. */
const SHOPIFY_API_KEYS_BY_SHOP = {
  "6b2c1f.myshopify.com": "41fa50cda9b5d074fbcbcff8810dab6b",
};

const DEFAULT_SHOPIFY_API_KEY = "5293edd182d0f478b2d21b028619a644";

let app;
let fetchPatched = false;
let axiosPatched = false;
const SESSION_TOKEN_TIMEOUT_MS = 8000;

export function resolveShopifyApiKey(shop) {
  const normalizedShop = String(shop || "")
    .trim()
    .toLowerCase()
    .replace(/^https?:\/\//, "")
    .split("/")[0];

  return SHOPIFY_API_KEYS_BY_SHOP[normalizedShop] || DEFAULT_SHOPIFY_API_KEY;
}

export function shouldBypassShopifyAppBridge() {
  return LOCAL_HOSTS.has(window.location.hostname);
}

function parseContextFromLocation() {
  const params = new URLSearchParams(window.location.search);

  return {
    host: params.get("host"),
    shop: params.get("shop"),
  };
}

function readStoredContext() {
  try {
    const value = window.sessionStorage.getItem(CONTEXT_STORAGE_KEY);
    return value ? JSON.parse(value) : {};
  } catch (error) {
    return {};
  }
}

function persistContext(context) {
  try {
    window.sessionStorage.setItem(CONTEXT_STORAGE_KEY, JSON.stringify(context));
  } catch (error) {
    // Ignore storage issues in embedded contexts.
  }
}

export function getShopifyContext() {
  const runtimeContext = parseContextFromLocation();
  const storedContext = readStoredContext();
  const mergedContext = {
    ...storedContext,
    ...Object.fromEntries(
      Object.entries(runtimeContext).filter(([, value]) => Boolean(value)),
    ),
  };

  if (mergedContext.host || mergedContext.shop) {
    persistContext(mergedContext);
  }

  return mergedContext;
}

function isApiRequest(requestUrl) {
  const normalizedUrl = new URL(requestUrl, window.location.origin);
  const apiBaseUrl = process.env.REACT_APP_API_URL;

  if (apiBaseUrl) {
    return normalizedUrl.href.startsWith(apiBaseUrl);
  }

  return normalizedUrl.pathname.startsWith("/api/");
}

function appendShopIfMissing(inputUrl) {
  const context = getShopifyContext();
  const normalizedUrl = new URL(inputUrl, window.location.origin);

  if (context.shop && !normalizedUrl.searchParams.has("shop")) {
    normalizedUrl.searchParams.set("shop", context.shop);
  }

  if (inputUrl.startsWith("http")) {
    return normalizedUrl.toString();
  }

  return `${normalizedUrl.pathname}${normalizedUrl.search}${normalizedUrl.hash}`;
}

export function buildEmbeddedPath(pathname) {
  const { shop, host } = getShopifyContext();
  const params = new URLSearchParams();

  if (shop) params.set("shop", shop);
  if (host) params.set("host", host);

  const query = params.toString();
  return query ? `${pathname}?${query}` : pathname;
}

function getAppInstance() {
  if (app) return app;

  const { host, shop } = getShopifyContext();
  const apiKey = resolveShopifyApiKey(shop);

  if (!host || !apiKey) {
    return null;
  }

  app = createApp({
    apiKey,
    host,
    forceRedirect: true,
  });

  return app;
}

async function getSessionTokenWithTimeout(appInstance) {
  let timeoutId;

  try {
    return await Promise.race([
      getSessionToken(appInstance),
      new Promise((resolve, reject) => {
        timeoutId = window.setTimeout(() => {
          reject(
            new Error(
              "Shopify session token was not available. Reload the app from Shopify Admin.",
            ),
          );
        }, SESSION_TOKEN_TIMEOUT_MS);
      }),
    ]);
  } finally {
    window.clearTimeout(timeoutId);
  }
}

async function authenticatedRequest(
  appInstance,
  nativeFetch,
  input,
  init = {},
) {
  const token = await getSessionTokenWithTimeout(appInstance);
  const headers = new Headers(init.headers);
  headers.set("Authorization", `Bearer ${token}`);
  headers.set("X-Requested-With", "XMLHttpRequest");

  return nativeFetch(input, { ...init, headers });
}

export function initializeShopifyApp() {
  if (shouldBypassShopifyAppBridge()) {
    if (!fetchPatched) {
      const nativeFetch = window.fetch.bind(window);

      window.fetch = (input, init = {}) => {
        const requestUrl =
          typeof input === "string"
            ? input
            : input instanceof URL
              ? input.href
              : input.url;

        if (!requestUrl || !isApiRequest(requestUrl)) {
          return nativeFetch(input, init);
        }

        return nativeFetch(appendShopIfMissing(requestUrl), {
          ...init,
          headers: {
            ...(init.headers || {}),
            "X-Dev-Local-Request": "1",
          },
        });
      };

      fetchPatched = true;
    }

    if (!axiosPatched) {
      axios.interceptors.request.use((config) => {
        const requestUrl = config.url || "";

        if (!requestUrl || !isApiRequest(requestUrl)) {
          return config;
        }

        config.url = appendShopIfMissing(requestUrl);
        config.headers = {
          ...config.headers,
          "X-Dev-Local-Request": "1",
        };

        return config;
      });

      axiosPatched = true;
    }

    return null;
  }

  const appInstance = getAppInstance();

  if (!appInstance) {
    return null;
  }

  if (!fetchPatched) {
    const nativeFetch = window.fetch.bind(window);

    window.fetch = (input, init) => {
      const requestUrl =
        typeof input === "string"
          ? input
          : input instanceof URL
            ? input.href
            : input.url;

      if (!requestUrl || !isApiRequest(requestUrl)) {
        return nativeFetch(input, init);
      }

      return authenticatedRequest(
        appInstance,
        nativeFetch,
        appendShopIfMissing(requestUrl),
        init,
      );
    };

    fetchPatched = true;
  }

  if (!axiosPatched) {
    axios.interceptors.request.use(async (config) => {
      const requestUrl = config.url || "";

      if (!requestUrl || !isApiRequest(requestUrl)) {
        return config;
      }

      const token = await getSessionTokenWithTimeout(appInstance);

      config.url = appendShopIfMissing(requestUrl);
      config.headers = {
        ...config.headers,
        Authorization: `Bearer ${token}`,
        "X-Requested-With": "XMLHttpRequest",
      };

      return config;
    });

    axiosPatched = true;
  }

  return appInstance;
}
